import { IUser } from "../../utils/types";
import { absoluteDateTimestamp } from "../../utils/timestamps";
import { escapeCsvValue, downloadCsv } from "../../utils/csvExport";
import { PRODUCT_DISPLAY_NAMES, STORE_DISPLAY_NAMES } from "./constants";

interface ColumnExportConfig {
  header: string;
  getValue: (user: IUser) => string | number;
}

const COLUMN_EXPORT_MAP: Record<string, ColumnExportConfig> = {
  id: { header: "User ID", getValue: u => u.id },
  name: { header: "Name", getValue: u => u.name ?? "" },
  email: { header: "Email", getValue: u => u.email ?? "" },
  roles: { header: "Roles", getValue: u => u.roles?.map(r => r.role).join(", ") ?? "" },
  status: { header: "Status", getValue: u => u.status ?? "" },
  createdAt: { header: "Created At", getValue: u => absoluteDateTimestamp(u.createdAt) },
  updatedAt: { header: "Updated At", getValue: u => absoluteDateTimestamp(u.updatedAt) },
  onboardingStatus: {
    header: "Onboarding Completed",
    getValue: u => (u.profile ? (u.profile.onboardingCompleted ? "Yes" : "No") : ""),
  },
  lastLoginAt: { header: "Last Login", getValue: u => (u.lastLoginAt ? absoluteDateTimestamp(u.lastLoginAt) : "") },
  mostRecentLoginCity: { header: "Login City", getValue: u => u.mostRecentLoginCity ?? "" },
  mostRecentLoginCountry: { header: "Login Country", getValue: u => u.mostRecentLoginCountry ?? "" },
  mostRecentLoginDevice: { header: "Login Device", getValue: u => u.mostRecentLoginDevice ?? "" },
  totalCulturesSubscribed: { header: "Cultures", getValue: u => u.totalCulturesSubscribed ?? 0 },
  totalTagsSubscribed: { header: "Interests", getValue: u => u.totalTagsSubscribed ?? 0 },
  subscribedAt: { header: "Subscribed Date", getValue: u => (u.subscribedAt ? absoluteDateTimestamp(u.subscribedAt) : "") },
  subscriptionStatus: {
    header: "Subscription Status",
    getValue: u => u.subscriptionStatus?.replace(/_/g, " ") ?? "",
  },
  customTrialStatus: { header: "Trial Status", getValue: u => u.customTrial?.status ?? "" },
  subscriptionProductId: {
    header: "Product",
    getValue: u => PRODUCT_DISPLAY_NAMES[u.subscriptionProductId ?? ""] ?? u.subscriptionProductId ?? "",
  },
  subscriptionStore: {
    header: "Store",
    getValue: u => STORE_DISPLAY_NAMES[u.subscriptionStore ?? ""] ?? u.subscriptionStore ?? "",
  },
  subscriptionStartAt: {
    header: "Billing Start",
    getValue: u => (u.subscriptionStartAt ? absoluteDateTimestamp(u.subscriptionStartAt) : ""),
  },
  subscriptionEndAt: { header: "Billing End", getValue: u => (u.subscriptionEndAt ? absoluteDateTimestamp(u.subscriptionEndAt) : "") },
  unsubscribedAt: { header: "Unsubscribed", getValue: u => (u.unsubscribedAt ? absoluteDateTimestamp(u.unsubscribedAt) : "") },
  totalLikes: { header: "Likes", getValue: u => u.totalLikes ?? 0 },
  totalHides: { header: "Hides", getValue: u => u.totalHides ?? 0 },
  totalShares: { header: "Shares", getValue: u => u.totalShares ?? 0 },
};

export const exportUsersToCsv = (users: IUser[], columnVisibility: Record<string, boolean>, filename = "users-export") => {
  // Always include id/name/email, then visible columns, skip "action"
  const alwaysVisible = ["id", "name", "email"];
  const columnKeys = [
    ...alwaysVisible,
    ...Object.keys(COLUMN_EXPORT_MAP).filter(key => !alwaysVisible.includes(key) && key !== "action" && columnVisibility[key] !== false),
  ];

  const configs = columnKeys.map(key => COLUMN_EXPORT_MAP[key]).filter(Boolean);

  const headers = configs.map(c => escapeCsvValue(c.header));
  const rows = users.map(user => configs.map(c => escapeCsvValue(c.getValue(user))));

  const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
  downloadCsv(csvContent, filename);
};
